import React from "react";
import GraphQLService from "../../services/graphql-service";
import GraphiQL from "graphiql";
import BaseWidget from "../widgets/base-widget/BaseWidget";
import SelectMenu, {OptionProps} from "../select/SelectMenu";
import {Endpoints} from '../../types/Configurations';

// @ts-ignore
import GraphiQLExplorer from "graphiql-explorer";

import "graphiql/graphiql.css";
import "./GraphiQLWrapper.scss";

type GraphiQLWrapperState = {
  fetcher: any,
  selectedEndpoint?: string,
  endpoints: Endpoints,
  isSingleEndpoint: boolean,
  explorerIsOpen: boolean,
  schema: any,
  query?: string
}

type GraphiQLWrapperProps = {
  endpoints: Endpoints;
}

/**
 * Wrapper for the GraphiQL playground application.
 */
export class GraphiQLWrapper extends React.Component<GraphiQLWrapperProps, GraphiQLWrapperState> {
  private readonly defaultEndpoint: string;
  private graphiql!: GraphiQL;

  constructor(props: any) {
    super(props);

    const endpoints = this.props.endpoints;
    // default to federation endpoint if provided, otherwise fallback to soaas
    this.defaultEndpoint = endpoints.federation?.endpoint || endpoints.soaas.endpoint;

    this.state = {
      fetcher: GraphQLService.getQueryExecutor(this.defaultEndpoint),
      selectedEndpoint: this.defaultEndpoint,
      endpoints: endpoints,
      isSingleEndpoint: Object.keys(endpoints).length === 1,
      explorerIsOpen: false,
      schema: null,
    };

    this.onEndpointSelectMenu = this.onEndpointSelectMenu.bind(this);
    this.handleEditQuery = this.handleEditQuery.bind(this);
    this.handleToggleExplorer = this.handleToggleExplorer.bind(this);
  }

  componentDidMount() {
    GraphQLService.graphqlIntrospection(this.defaultEndpoint).then((schema: any) => {
      this.setState({schema});
    })
  }

  onEndpointSelectMenu(endpoint: string) {
    GraphQLService.graphqlIntrospection(endpoint).then((schema: any) => {
      this.setState({
        selectedEndpoint: endpoint,
        fetcher: GraphQLService.getQueryExecutor(endpoint),
        schema
      });
    });
  }

  private handleToggleExplorer() {
    this.setState({
      explorerIsOpen: !this.state.explorerIsOpen
    });
  }

  private handleEditQuery(query?: string): void {
    this.setState({ query });
  }

  render() {
    const { query, schema, explorerIsOpen } = this.state;

    return (
      <>
        {this.state.isSingleEndpoint || <EndpointSelectMenu endpoints={this.state.endpoints}
                                                 selected={this.state.selectedEndpoint}
                                                 onChange={this.onEndpointSelectMenu}/>}
        <BaseWidget className="GraphiQLWrapper" dropShadow={true}>
          <div className="graphiql-container graphiql-explorer">
            <GraphiQLExplorer
              schema={schema}
              query={query}
              onEdit={(q: string) => this.setState({ query: q })}
              onRunOperation={(opName: string) => this.graphiql.handleRunQuery(opName)}
              explorerIsOpen={explorerIsOpen}
              onToggleExplorer={this.handleToggleExplorer}
              onEditQuery={this.handleEditQuery}
            />
          </div>

          <GraphiQL
            fetcher={this.state.fetcher}
            ref={(ref: GraphiQL) => this.graphiql = ref}
            schema={schema}
            query={query}
            onEditQuery={this.handleEditQuery}
            onCopyQuery={(q: string | undefined): void => {}}>

            <GraphiQL.Toolbar>
              <GraphiQL.Button
                onClick={() => this.graphiql.handlePrettifyQuery()}
                label="Prettify"
                title="Prettify Query (Shift-Ctrl-P)"
              />
              <GraphiQL.Button
                onClick={() => this.graphiql.handleMergeQuery()}
                label="Merge"
                title="Merge (Shift-Ctrl-M)"
              />
              <GraphiQL.Button
                onClick={() => this.graphiql.handleCopyQuery()}
                label="Copy"
                title="Copy (Shift-Ctrl-C)"
              />
              <GraphiQL.Button
                onClick={() => this.graphiql.handleToggleHistory()}
                label="History"
                title="Show History"
              />
              <GraphiQL.Button
                onClick={this.handleToggleExplorer}
                label="Explorer"
                title="Toggle Explorer"
              />
            </GraphiQL.Toolbar>
          </GraphiQL>
        </BaseWidget>
      </>
    );
  }

}

function EndpointSelectMenu(props: {
  endpoints: Endpoints,
  selected: string | undefined,
  onChange: (selection: string) => void
}) {
  const options: Array<OptionProps> = Object.keys(props.endpoints).map(key => {
    return {
      id: key,
      label: props.endpoints[key].label
    };
  });
  return (
    <SelectMenu className="EndpointSelectMenu"
                options={options}
                selected={props.selected}
                onChange={props.onChange}/>
  );
}
